/* eslint-disable @typescript-eslint/no-explicit-any -- getStats() devolve stats sem tipos no DOM */
/**
 * Logger de diagnóstico do encoder/BWE do publisher (TV — qualidade da gravação).
 * Atrás de REACT_APP_RTC_STATS_DEBUG (default off). Amostra getStats() a partir do publish e:
 *   (a) loga no console (granularidade fina), e
 *   (b) GRAVA no histórico da regulação numa cadência menor (saveEventHistory → apiToken.cfm
 *       action=registraHistorico, a MESMA trilha que o app já usa p/ eventos; prefixo [RTC-DIAG])
 *       — o perito vê a curva em tempo real na tela, sem chrome://inspect no celular.
 * Coleta TODAS as camadas de simulcast (cada outbound-rtp de vídeo) + media-source (fonte) +
 * candidate-pair (BWE/RTT, SEM IPs). Separa as causas de resolução baixa:
 *   - media-source baixa                         → captura/constraints
 *   - fonte alta, camadas baixas + BWE baixo     → BWE não rampou / start bitrate
 *   - sobe e depois colapsa                       → reconfiguração da câmera (takePhoto)
 */
import type { Publisher } from "openvidu-browser-v2compatibility";
import { saveEventHistory } from "../services/legacyService";

const RTC_STATS_DEBUG =
  process.env.REACT_APP_RTC_STATS_DEBUG === "1" ||
  process.env.REACT_APP_RTC_STATS_DEBUG === "true";

interface RtcStatsLoggerOptions {
  intervalMs?: number; // amostragem p/ console (padrão 1000 = 1s)
  postIntervalMs?: number; // cadência de gravação no histórico (padrão 5000 = 5s, p/ não poluir)
  maxSamples?: number; // teto de amostras (padrão 60 ~= 1 min)
  pcWaitMs?: number; // espera máx. pelo RTCPeerConnection aparecer (padrão 15s)
}

/** Inicia o logger; devolve uma função para encerrar (segura como cleanup de useEffect). */
export function startSenderStatsLogger(
  publisher?: Publisher | null,
  options: RtcStatsLoggerOptions = {}
): () => void {
  if (!RTC_STATS_DEBUG || !publisher) return () => {};

  const intervalMs = options.intervalMs ?? 1000;
  const postIntervalMs = options.postIntervalMs ?? 5000;
  const maxSamples = options.maxSamples ?? 60;
  const pcWaitMs = options.pcWaitMs ?? 15000;

  // sessionId da sala = token_regulacao. É lido A CADA tick (não uma vez no início): o logger
  // arranca no publisher, mas o sessionId só existe depois do session.connect — ler uma vez
  // pegaria undefined e NUNCA gravaria no histórico. Sem token ainda: só console (até conectar).
  const getToken = (): string | undefined =>
    (publisher as unknown as { session?: { sessionId?: string } }).session?.sessionId;

  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let samples = 0;
  let lastPostAt = 0;
  const t0 = performance.now();
  // bytesSent anterior por camada (stat.id) p/ calcular kbps efetivo de cada layer
  const prevBytes = new Map<string, { bytes: number; ts: number }>();

  const getPc = (): RTCPeerConnection | undefined => {
    try {
      return publisher.stream?.getRTCPeerConnection?.() ?? undefined;
    } catch {
      return undefined;
    }
  };

  const schedule = () => {
    if (!stopped) timer = setTimeout(() => void tick(), intervalMs);
  };

  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (timer) clearTimeout(timer);
    timer = null;
    console.info("[rtcStats] logger encerrado");
  };

  const fmtLayer = (s: any, effK: number | undefined): string => {
    const rid = s.rid ?? "-";
    const wh = `${s.frameWidth ?? "?"}x${s.frameHeight ?? "?"}`;
    const fps = s.framesPerSecond != null ? Math.round(s.framesPerSecond) : "?";
    const tgt = s.targetBitrate != null ? Math.round(s.targetBitrate / 1000) : "?";
    const parts = [`L[${rid}]=${wh}@${fps} tgt${tgt}k eff${effK ?? "?"}k`];
    if (s.qualityLimitationReason) parts.push(`lim=${s.qualityLimitationReason}`);
    if (s.nackCount != null) parts.push(`nack${s.nackCount}`);
    if (s.pliCount != null) parts.push(`pli${s.pliCount}`);
    if (s.retransmittedPacketsSent != null) parts.push(`rtx${s.retransmittedPacketsSent}`);
    if (s.encoderImplementation) parts.push(String(s.encoderImplementation));
    if (s.active === false) parts.push("INATIVA");
    return parts.join(" ");
  };

  const tick = async () => {
    if (stopped) return;

    const pc = getPc();
    if (!pc) {
      if (performance.now() - t0 > pcWaitMs) {
        console.warn("[rtcStats] RTCPeerConnection não apareceu — encerrando");
        stop();
        return;
      }
      schedule(); // ainda publicando: tenta de novo sem contar amostra
      return;
    }
    if (pc.connectionState === "closed" || pc.connectionState === "failed") {
      stop();
      return;
    }

    try {
      const report = await pc.getStats();
      const outbounds: any[] = [];
      let src: any;
      let pair: any;
      report.forEach((s: any) => {
        if (s.type === "outbound-rtp" && (s.kind === "video" || s.mediaType === "video")) {
          outbounds.push(s);
        } else if (s.type === "media-source" && (s.kind === "video" || s.mediaType === "video")) {
          src = s;
        } else if (
          s.type === "candidate-pair" &&
          s.availableOutgoingBitrate != null &&
          (s.state === "succeeded" || s.nominated || s.selected)
        ) {
          pair = s;
        }
      });
      outbounds.sort((a, b) => (b.frameHeight ?? 0) - (a.frameHeight ?? 0)); // maior camada primeiro

      const tSec = ((performance.now() - t0) / 1000).toFixed(0);
      const srcFps = src?.framesPerSecond != null ? Math.round(src.framesPerSecond) : "?";
      const srcWh = src?.width && src?.height ? `${src.width}x${src.height}@${srcFps}` : "?";
      const bwe = pair?.availableOutgoingBitrate != null ? Math.round(pair.availableOutgoingBitrate / 1000) : "?";
      const rtt = pair?.currentRoundTripTime != null ? Math.round(pair.currentRoundTripTime * 1000) : "?";

      const layerStrs = outbounds.map((s) => {
        let effK: number | undefined;
        if (typeof s.bytesSent === "number" && typeof s.timestamp === "number") {
          const prev = prevBytes.get(s.id);
          // RTCStats.timestamp é DOMHighResTimeStamp em MS (spec). dt>=50ms evita divisão por ~0
          // e ruído (os ticks são ~1s, então o dt real fica ~1000ms).
          const dt = prev ? s.timestamp - prev.ts : 0;
          if (prev && dt >= 50) {
            effK = Math.round(((s.bytesSent - prev.bytes) * 8) / dt); // bytes*8/ms = kbps
          }
          prevBytes.set(s.id, { bytes: s.bytesSent, ts: s.timestamp });
        }
        return fmtLayer(s, effK);
      });

      const line =
        `RTC t=${tSec}s src=${srcWh} | BWE=${bwe}k rtt=${rtt}ms | ` +
        `${layerStrs.length ? layerStrs.join(" | ") : "(sem outbound-rtp de vídeo)"}`;
      console.info(`[rtcStats] ${line}`);

      // Grava no histórico a cada postIntervalMs (cadência menor que o console p/ não poluir).
      const now = performance.now();
      const token = getToken();
      if (token && now - lastPostAt >= postIntervalMs) {
        lastPostAt = now;
        // "como o app já faz": registraHistorico legado (apiToken.cfm) — mesma trilha dos eventos.
        saveEventHistory(`[RTC-DIAG] ${line}`, token).catch(() => {
          /* fire-and-forget: diagnóstico nunca derruba a chamada */
        });
      }
    } catch (error) {
      console.warn("[rtcStats] getStats falhou", error);
    }

    samples += 1;
    if (samples >= maxSamples) {
      stop();
      return;
    }
    schedule();
  };

  console.info(
    `[rtcStats] logger iniciado (console ${intervalMs}ms, histórico ${postIntervalMs}ms, ${maxSamples} amostras)`
  );
  void tick();
  return stop;
}
